/** Mirrors backend utils/role_page_access.py — page keys per system role. */

export const SYSTEM_ROLES = ["admin", "hr", "manager", "employee"];

export const ROLE_META = {
  admin: {
    label: "Super Admin",
    description: "Organisation owner — all live modules, settings, and payroll controls.",
    color: "#4f46e5",
    gradient: "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)",
  },
  hr: {
    label: "HR",
    description: "People, payroll, attendance administration, and lifecycle.",
    color: "#0891b2",
    gradient: "linear-gradient(135deg, #0891b2 0%, #06b6d4 100%)",
  },
  manager: {
    label: "Manager",
    description: "Team approvals, team attendance, and personal self-service.",
    color: "#d97706",
    gradient: "linear-gradient(135deg, #d97706 0%, #f59e0b 100%)",
  },
  employee: {
    label: "Employee",
    description: "Personal attendance, leave, payslips, and onboarding.",
    color: "#64748b",
    gradient: "linear-gradient(135deg, #475569 0%, #64748b 100%)",
  },
};

/** Legacy keys from the old 3-page employee UI — all map to unified Employees. */
export const EMPLOYEE_PAGE_KEYS = new Set(["people_employees", "emp_list", "emp_add", "emp_input"]);

/** Any leave-module page key grants access to the unified Leave Dashboard. */
export const LEAVE_DASHBOARD_PAGE_KEYS = new Set([
  "lv_dashboard",
  "lv_apply",
  "lv_approval",
  "lv_balance",
  "lv_analytics",
  "lv_admin",
]);

export function hasLeaveDashboardAccess(pageAccess) {
  const pageKeys = pageAccess?.page_keys || [];
  return pageKeys.some((k) => LEAVE_DASHBOARD_PAGE_KEYS.has(k));
}

export function hasEmployeePageAccess(pageAccess) {
  const pageKeys = pageAccess?.page_keys || [];
  return pageKeys.some((k) => EMPLOYEE_PAGE_KEYS.has(k));
}

/** Map sidebar item keys to permission-template page keys. */
export const SIDEBAR_KEY_ALIASES = {
  people_hub: "people_employees",
  people_employees: "people_employees",
  emp_hierarchy: "emp_hierarchy",
  org_structure: "org_structure",
  emp_list: "people_employees",
  emp_add: "people_employees",
  emp_input: "people_employees",
  rpt_employees: "people_employees",
  rpt_hub: "people_employees",
  settings_main: "settings",
  settings_hub: "settings",
  settings_org_profile: "settings",
  settings_payroll: "settings",
  settings_extra_work: "settings",
  settings_salary_components: "settings",
  settings_departments: "settings",
  settings_designations: "settings",
  settings_locations: "settings",
  settings_org_structure: "settings",
  settings_employee_numbering: "settings",
  settings_employee_setup: "settings",
  settings_employment_types: "settings",
  settings_attendance_config: "settings",
  settings_shifts: "settings",
  settings_statutory: "settings_statutory",
  settings_holiday_calendar: "settings",
  settings_users_roles: "settings",
  settings_security: "settings",
  settings_general: "settings",
  statutory_components: "settings_statutory",
  stat_hub: "settings_statutory",
  stat_pf: "settings_statutory",
  stat_esi: "settings_statutory",
  stat_pt: "settings_statutory",
  stat_lwf: "settings_statutory",
  stat_tds: "settings_statutory",
  emp_my_onboarding: "my_onboarding",
  emp_my_exit: "my_exit",
  pay_process_hub: "pay_process",
  pay_periods: "pay_process",
  pay_payroll_calendar: "pay_process",
  emp_payslip: "my_payslip",
  emp_fuel: "my_fuel",
  emp_home: "dashboard",
  emp_att_my: "my_attendance",
  emp_holidays: "lv_calendar",
  emp_leave_my: "lv_dashboard",
  emp_comp_off: "lv_comp_off",
  lv_comp_off: "lv_comp_off",
  mgr_leave_approval: "lv_approval",
  // Assign Leave has no page key of its own; it follows "Approve Leave" access.
  lv_assign: "lv_approval",
  lv_dashboard: "lv_dashboard",
  lv_apply: "lv_apply",
  lv_approval: "lv_approval",
  lv_balance: "lv_balance",
  lv_analytics: "lv_analytics",
  lv_admin: "lv_admin",
  mgr_org_hierarchy: "mgr_team",
  mgr_ot_approval: "att_approvals",
  att_ot_hours: "att_hub",
  att_dash: "att_hub",
  att_mark: "att_hub",
  att_records: "att_hub",
  att_cal: "att_hub",
  att_holiday: "att_hub",
  att_devices: "att_hub",
  att_device_mapping: "att_hub",
  att_shifts: "att_shifts",
  comp_statutory: "statutory",
  rpt_compliance: "statutory",
  rpt_attendance: "att_summary",
  rpt_leave: "lv_analytics",
  rpt_payroll: "pay_register",
  audit_logs: "audit",
  bank_requests: "bank",
  adv_bank_payments: "bank",
  lc_dashboard: "lifecycle",
  emp_lifecycle: "lifecycle",
  lc_onboarding: "lifecycle",
  lc_offboarding: "lifecycle",
  lc_templates: "lifecycle",
  lc_analytics: "lifecycle",
  lc_audit: "lifecycle",
};
